import { FC } from 'react'
import { FieldArray, ErrorMessage } from 'formik'

import { getBreaksToApply } from 'services/schedule'
import { useBreaks } from 'Api'
import { Button, Checkbox, Input } from 'components/Forms'
import { Button as MerinioButton, ButtonGroup, Card, Dropdown } from 'ui'

import { t } from 'translations'

type Props = {
  name: string
  breaks: MData.ShiftBreak[]
  event?: MData.Event
  setFieldValue: (field: string, value: Partial<MData.Break>[]) => void
  allowRecalculate?: boolean
  required: boolean
}

const Breaks: FC<Props> = ({ breaks, event, setFieldValue, allowRecalculate, required }) => {
  const { data } = useBreaks()
  const breakTypes = data?.breaks || []

  return (
    <>
      <h5>
        {t('manager.schedule.eventForm.BREAKS.TITLE')}
        {required ? <span className="text-danger">*</span> : null}
      </h5>
      <small className="form-text text-muted">{t('manager.schedule.eventForm.BREAKS.SUMMARY')}</small>

      <FieldArray
        name="breaks"
        render={arrayHelpers => (
          <div className="mb-3">
            <Card className="bg-light">
              <div className="p-3">
                {breaks.map((_, index) => (
                  <div key={index} className="row">
                    <Input
                      module="manager.schedule.eventForm"
                      name={`breaks[${index}].duration`}
                      required
                      type="number"
                      min="0"
                      max="1440"
                      translation="breakDuration"
                      fieldClass="col-4"
                    />
                    <Input module="manager.schedule.eventForm" name={`breaks[${index}].name`} required translation="breakName" fieldClass="col-4" />
                    <div className="col-3" style={{ marginTop: '32px' }}>
                      <Checkbox module="manager.schedule.eventForm" name={`breaks[${index}].paid`} translation="breakPaid" />
                    </div>
                    <div className="col-1" style={{ marginTop: '32px' }}>
                      <MerinioButton
                        type="button"
                        color="secondary"
                        size="small"
                        className="array-input-button"
                        onClick={() => {
                          arrayHelpers.remove(index)
                        }}
                        outlined
                      >
                        <i className="fas fa-trash" />
                      </MerinioButton>
                    </div>
                  </div>
                ))}
                <ButtonGroup>
                  <Dropdown.Toggle id="tools" color="secondary" outlined>
                    <i className="fas fa-plus mr-1" />
                    {t('manager.schedule.eventForm.BREAKS.ADD_BREAK')}
                  </Dropdown.Toggle>
                  <Dropdown.Menu direction="right">
                    <Dropdown.Item
                      onClick={() =>
                        arrayHelpers.push({ name: t('manager.schedule.eventForm.BREAKS.CUSTOM_NAME', { index: breaks.length + 1 }), duration: 30, paid: true })
                      }
                    >
                      {t('manager.schedule.eventForm.BREAKS.CUSTOM')}
                    </Dropdown.Item>
                    {breakTypes.map(({ _id, name, duration, paid }) => (
                      <Dropdown.Item key={_id} onClick={() => arrayHelpers.push({ name, duration, paid: !!paid })}>
                        {name}
                      </Dropdown.Item>
                    ))}
                  </Dropdown.Menu>
                </ButtonGroup>
                {allowRecalculate && event ? (
                  <Button
                    text="manager.schedule.eventForm.BREAKS.RECALCULATE_BREAKS"
                    className="btn btn-outline-secondary ml-1"
                    onClick={() => setFieldValue('breaks', getBreaksToApply(event))}
                  />
                ) : null}
              </div>
            </Card>
            {/* Will work when validation is added to event modal (YUP) */}
            <ErrorMessage name="breaks" render={msg => (Array.isArray(msg) ? null : <span className="text-danger"> {t(msg)}</span>)} />
          </div>
        )}
      />
    </>
  )
}

export default Breaks
